import { AssessmentConfig } from '../types/assessment'

export type FontFamilyValue = NonNullable<AssessmentConfig['pdfOptions']['fontFamily']>

export type FontSizeValue = AssessmentConfig['pdfOptions']['fontSize']

export interface FontUrls {
  regular: string
  bold: string
  italic: string
}

/** Os quatro níveis tipográficos que o gerador desenha, em pt. */
export interface FontSizesPt {
  title: number
  subtitle: number
  normal: number
  small: number
}

/**
 * Lista ÚNICA de famílias de fonte do PDF. Antes existiam 3 listas
 * divergentes (FontSettings sem 'liberation' → nenhum radio selecionado
 * quando era a fonte ativa; PDFDownloadWizard com 5; types com 5).
 * Sem "prévia" visual: as webfonts correspondentes não são carregadas no
 * app, então toda prévia renderizava idêntica na fonte de fallback.
 */
export const FONT_FAMILIES: ReadonlyArray<{
  value: FontFamilyValue
  label: string
  description: string
  isDefault?: boolean
}> = [
  {
    value: 'tinos',
    label: 'Times New Roman',
    description: 'Métricas idênticas às do Word — mesmas quebras de linha',
    isDefault: true
  },
  {
    value: 'dejavu',
    label: 'DejaVu Sans',
    description: 'Excelente para textos científicos e matemáticos'
  },
  {
    value: 'liberation',
    label: 'Liberation Sans',
    description: 'Métricas compatíveis com Arial, amplo suporte Unicode'
  },
  {
    value: 'noto',
    label: 'Noto Sans',
    description: 'Fonte universal do Google com amplo suporte Unicode'
  },
  {
    value: 'roboto',
    label: 'Roboto',
    description: 'Moderna e limpa, estilo Material Design'
  },
  {
    value: 'merriweather',
    label: 'Merriweather',
    description: 'Elegante com serifa, ideal para leitura prolongada'
  }
]

/**
 * Registro ÚNICO de URLs. Antes vivia duplicado em pdfLibGenerator.getFontUrls()
 * e fontPreloader.FONT_URLS, com um comentário pedindo sincronia manual — que
 * não aconteceu: o preloader aquecia o cache de uma URL e o gerador baixava
 * outra. Ambos consomem daqui agora.
 *
 * SÓ TTF/OTF. O PDF embute o arquivo de fonte cru num stream /FontFile2, e
 * nenhum leitor de PDF sabe ler WOFF/WOFF2 — o fontkit até parseia, então o
 * build passa, mas o leitor descarta a fonte e substitui por outra, mantendo
 * a tabela /Widths da original: sai texto com vãos no meio das palavras.
 * O ecossistema npm de webfonts é woff2-only (fontsource, typeface-*), por
 * isso os TTF vêm do repositório oficial google/fonts, pinados por commit —
 * as versões atuais só publicam fontes variáveis, que não têm bold real.
 * Ver assertEmbeddableFont() abaixo: a trava em runtime.
 */
export const FONT_URLS: Record<FontFamilyValue, FontUrls> = {
  // Clone métrico livre do Times New Roman: avanços idênticos aos do
  // times.ttf do Windows (razão 1,00000) e mesma altura de linha natural.
  tinos: {
    regular: 'https://cdn.jsdelivr.net/gh/google/fonts@ba95515f1333efe9342c2ad988b9c2f6bef6dbad/ofl/tinos/Tinos-Regular.ttf',
    bold: 'https://cdn.jsdelivr.net/gh/google/fonts@ba95515f1333efe9342c2ad988b9c2f6bef6dbad/ofl/tinos/Tinos-Bold.ttf',
    italic: 'https://cdn.jsdelivr.net/gh/google/fonts@ba95515f1333efe9342c2ad988b9c2f6bef6dbad/ofl/tinos/Tinos-Italic.ttf'
  },
  dejavu: {
    regular: 'https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans.ttf',
    bold: 'https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans-Bold.ttf',
    italic: 'https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans-Oblique.ttf'
  },
  // Liberation usa DejaVu (excelente suporte Unicode)
  liberation: {
    regular: 'https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans.ttf',
    bold: 'https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans-Bold.ttf',
    italic: 'https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans-Oblique.ttf'
  },
  // noto-sans@1.0.3 (a URL anterior) saiu do ar: 404 → fallback silencioso.
  noto: {
    regular: 'https://cdn.jsdelivr.net/gh/google/fonts@1fc9ccab9c73/ofl/notosans/NotoSans-Regular.ttf',
    bold: 'https://cdn.jsdelivr.net/gh/google/fonts@1fc9ccab9c73/ofl/notosans/NotoSans-Bold.ttf',
    italic: 'https://cdn.jsdelivr.net/gh/google/fonts@1fc9ccab9c73/ofl/notosans/NotoSans-Italic.ttf'
  },
  // roboto-fontface@0.10.0 não publicava o itálico (404); como o gerador
  // baixa as três num Promise.all, a família inteira caía no fallback.
  roboto: {
    regular: 'https://cdn.jsdelivr.net/gh/google/fonts@3b947ee2732103ef6132b09d234aecd768d2d83c/apache/roboto/static/Roboto-Regular.ttf',
    bold: 'https://cdn.jsdelivr.net/gh/google/fonts@3b947ee2732103ef6132b09d234aecd768d2d83c/apache/roboto/static/Roboto-Bold.ttf',
    italic: 'https://cdn.jsdelivr.net/gh/google/fonts@3b947ee2732103ef6132b09d234aecd768d2d83c/apache/roboto/static/Roboto-Italic.ttf'
  },
  merriweather: {
    regular: 'https://cdn.jsdelivr.net/gh/google/fonts@787dfb1fe381/ofl/merriweather/Merriweather-Regular.ttf',
    bold: 'https://cdn.jsdelivr.net/gh/google/fonts@787dfb1fe381/ofl/merriweather/Merriweather-Bold.ttf',
    italic: 'https://cdn.jsdelivr.net/gh/google/fonts@787dfb1fe381/ofl/merriweather/Merriweather-Italic.ttf'
  }
}

/**
 * Rejeita bytes que o PDF não consegue embutir. Chamada no caminho de carga
 * ANTES do embedFont, inclusive sobre o que vem do cache — um cache
 * envenenado com woff2 sobreviveria a qualquer troca de URL.
 *
 * Falhar aqui é melhor que suceder: o gerador cai no fallback e imprime numa
 * fonte legível, em vez de produzir um PDF de aparência correta no build e
 * ilegível na mão do professor.
 */
export function assertEmbeddableFont(bytes: ArrayBuffer, url: string): void {
  const magic = new Uint8Array(bytes.slice(0, 4))
  const tag = String.fromCharCode(...magic)

  if (tag === 'wOFF' || tag === 'wOF2') {
    throw new Error(
      `Fonte em formato ${tag} não pode ser embutida em PDF (só TTF/OTF): ${url}`
    )
  }

  const isTrueType =
    (magic[0] === 0x00 && magic[1] === 0x01 && magic[2] === 0x00 && magic[3] === 0x00) ||
    tag === 'true' ||
    tag === 'ttcf'
  const isCFF = tag === 'OTTO'

  if (!isTrueType && !isCFF) {
    throw new Error(`Arquivo de fonte não reconhecido (${JSON.stringify(tag)}): ${url}`)
  }
}

/**
 * Família cuja escala de pt é tomada ao pé da letra. Tinos = Times New Roman,
 * então `medium` nela são exatamente os 12pt do Word.
 */
export const REFERENCE_FONT: FontFamilyValue = 'tinos'

/**
 * Altura-x de cada família, em fração do em. É o que o olho lê como "tamanho":
 * duas fontes no mesmo corpo em pt parecem tamanhos diferentes se as alturas-x
 * divergirem (DejaVu 12pt parece ~22% maior que Times 12pt).
 *
 * Medido com fontkit sobre os próprios arquivos servidos pelas URLs acima:
 * bbox do glifo 'x' ÷ unitsPerEm. Ao trocar uma URL, remedir.
 */
export const FONT_X_HEIGHT: Record<FontFamilyValue, number> = {
  tinos: 0.4590,
  dejavu: 0.5469,
  liberation: 0.5469, // carrega DejaVu
  noto: 0.5360,
  roboto: 0.5283,
  merriweather: 0.5550
}

/** Escala nominal, em pt, ancorada no Word (o corpo é `normal`). */
const NOMINAL_SIZES: Record<FontSizeValue, FontSizesPt> = {
  small: { title: 14, subtitle: 12, normal: 11, small: 9 },
  medium: { title: 16, subtitle: 14, normal: 12, small: 10 },
  large: { title: 18, subtitle: 16, normal: 14, small: 11 }
}

/**
 * Quanto multiplicar os pt nominais para a família desenhar o mesmo tamanho
 * aparente da referência. 1,0 na própria referência; < 1 nas sans, que têm
 * altura-x maior.
 */
export function fontScaleFactor(fontFamily?: FontFamilyValue): number {
  const xHeight = fontFamily ? FONT_X_HEIGHT[fontFamily] : undefined
  if (!xHeight) return 1
  return FONT_X_HEIGHT[REFERENCE_FONT] / xHeight
}

/**
 * Resolve os quatro níveis em pt para uma família + tamanho.
 *
 * Fonte ÚNICA da escala: o gerador de PDF, a prévia do editor e o seletor de
 * tipografia chamam esta função. Antes cada um tinha sua própria tabela e
 * ninguém aplicava o fator por família, então trocar de fonte mudava
 * silenciosamente o tamanho impresso.
 */
export function resolveFontSizes(
  fontFamily: FontFamilyValue | undefined,
  fontSize: FontSizeValue
): FontSizesPt {
  const nominal = NOMINAL_SIZES[fontSize] ?? NOMINAL_SIZES.medium
  const factor = fontScaleFactor(fontFamily)

  return {
    title: nominal.title * factor,
    subtitle: nominal.subtitle * factor,
    normal: nominal.normal * factor,
    small: nominal.small * factor
  }
}

/** URLs da família, caindo na referência quando o valor é desconhecido. */
export function getFontUrls(fontFamily?: string): FontUrls {
  const key = fontFamily?.toLowerCase() as FontFamilyValue | undefined
  return (key && FONT_URLS[key]) || FONT_URLS[REFERENCE_FONT]
}
